import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
// Helper component for the vehicle card
function VehicleCard({
  vehicle
}: {
  vehicle: any;
}) {
  return <Card className="rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border-none bg-white flex flex-col h-full" data-id="element-397">
      <img src={vehicle.image} alt={vehicle.name} className="w-full h-48 object-cover" data-id="element-398" />
      <div className="p-6 flex flex-col flex-grow" data-id="element-399">
        <div className="flex justify-between items-start gap-2 mb-2" data-id="element-400">
          <h3 className="text-lg font-bold text-black leading-tight min-h-[2.75rem]" data-id="element-401">
            {vehicle.name}
          </h3>
          <span className="bg-warm-100 text-warm-800 text-xs font-bold px-2 py-1 rounded flex-shrink-0" data-id="element-402">
            {vehicle.year}
          </span>
        </div>
        <p className="text-primary-600 font-bold text-lg mb-3" data-id="element-403">
          {vehicle.price}
        </p>
        <p className="text-warm-600 text-sm mb-6 flex-grow line-clamp-2" data-id="element-404">
          {vehicle.description}
        </p>
        <div className="flex gap-3 mt-auto items-stretch" data-id="element-405">
          <Link to="/booking" className="flex-1 flex" data-id="element-406">
            <Button variant="primary" size="sm" className="w-full h-full" data-id="element-407">
              Call
            </Button>
          </Link>
          <Link to="/booking" className="flex-1 flex" data-id="element-408">
            <Button variant="outline" size="sm" className="w-full h-full border-2" data-id="element-409">
              Hire Now
            </Button>
          </Link>
        </div>
      </div>
    </Card>;
}
export function Fleet() {
  const cars = [{
    name: 'Honda Odyssey Accessible',
    year: '2024',
    price: '$35/hr',
    description: 'Modified with side-entry ramp. Perfect for affordable passenger transport or shipping small mobility scooters.',
    image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0afa?w=600&q=80'
  }, {
    name: 'Toyota Camry Modified',
    year: '2023',
    price: '$30/hr',
    description: 'Comfortable, budget-friendly sedan with specialized hand controls and transfer seat.',
    image: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=600&q=80'
  }, {
    name: 'Chrysler 200 Accessible',
    year: '2022',
    price: '$28/hr',
    description: 'Our most affordable compact option with customized accessibility features.',
    image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600&q=80'
  }, {
    name: 'Hyundai Sonata Modified',
    year: '2025',
    price: '$32/hr',
    description: 'Modern sedan equipped with state-of-the-art mobility aids at a low rate.',
    image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&q=80'
  }];
  const vans = [{
    name: 'Chrysler Pacifica Accessible',
    year: '2025',
    price: '$45/hr',
    description: 'Premium minivan with power in-floor ramp. Ideal for heavy wheelchair shipping or passenger comfort.',
    image: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=600&q=80'
  }, {
    name: 'Toyota Sienna Modified',
    year: '2024',
    price: '$40/hr',
    description: 'Hybrid efficiency allows us to pass fuel savings to you. Features manual fold-out ramp.',
    image: 'https://images.unsplash.com/photo-1570733577524-3a047079e80d?w=600&q=80'
  }, {
    name: 'Honda Odyssey WAV',
    year: '2023',
    price: '$38/hr',
    description: 'Reliable, affordable wheelchair accessible van with rear-entry configuration for easy loading.',
    image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=600&q=80'
  }, {
    name: 'Dodge Grand Caravan',
    year: '2022',
    price: '$35/hr',
    description: 'Classic accessible van offering our most budget-friendly wheelchair transport and shipping rates.',
    image: 'https://images.unsplash.com/photo-1543465077-db45d34b88a5?w=600&q=80'
  }];
  const trucks = [{
    name: 'Ford F-150 Accessible',
    year: '2024',
    price: '$55/hr',
    description: 'Rugged capability with specialized lift system. Excellent for shipping oversized medical equipment.',
    image: 'https://images.unsplash.com/photo-1583267746897-2cf415887172?w=600&q=80'
  }, {
    name: 'Chevrolet Silverado Modified',
    year: '2023',
    price: '$50/hr',
    description: 'Heavy-duty truck featuring an automated wheelchair lift at competitive rates.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=600&q=80'
  }, {
    name: 'RAM 1500 Accessible',
    year: '2025',
    price: '$58/hr',
    description: 'Luxury interior with seamless mobility modifications, available for affordable daily rentals.',
    image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80'
  }, {
    name: 'Toyota Tundra Modified',
    year: '2022',
    price: '$48/hr',
    description: 'Reliable full-size truck with custom transfer seat installation and large cargo bed for shipping.',
    image: 'https://images.unsplash.com/photo-1612544448445-b8232cff3b6c?w=600&q=80'
  }];
  const suvs = [{
    name: 'Ford Explorer Accessible',
    year: '2024',
    price: '$45/hr',
    description: 'Spacious SUV with customized rear-entry wheelchair ramp for affordable family transport.',
    image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=600&q=80'
  }, {
    name: 'Chevrolet Traverse Modified',
    year: '2023',
    price: '$40/hr',
    description: 'Mid-size crossover with lowered floor and side-entry access at budget-friendly rates.',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=600&q=80'
  }, {
    name: 'Honda Pilot WAV',
    year: '2025',
    price: '$48/hr',
    description: 'Family-friendly SUV featuring a power lift and securement system at competitive rates.',
    image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600&q=80'
  }, {
    name: 'Hyundai Palisade Accessible',
    year: '2024',
    price: '$50/hr',
    description: 'Premium crossover offering luxurious mobility with advanced safety at affordable rates.',
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80'
  }];
  return <div className="bg-white min-h-screen" data-id="element-410">
      {/* Page Header */}
      <div className="bg-white py-16 border-b border-warm-200" data-id="element-411">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-412">
          <h1 className="text-4xl md:text-5xl font-extrabold text-black mb-4" data-id="element-413">
            Our Fleet
          </h1>
          <p className="text-xl text-warm-600 max-w-2xl mx-auto" data-id="element-414">
            Discover our diverse range of fully modified, wheelchair-accessible
            vehicles designed for your comfort and safety.
          </p>
        </div>
      </div>

      {/* Section 1: Cars */}
      <section className="py-20 bg-white" data-id="element-415">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-416">
          <div className="mb-12" data-id="element-417">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-418">
              Wheelchair Accessible Cars
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-419">
              Perfect for everyday errands and medical appointments, our
              accessible cars offer a smooth, efficient ride with specialized
              modifications for easy transfer and securement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-420">
            {cars.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-421" />)}
          </div>
          <div className="text-center" data-id="element-422">
            <Link to="/fleet/cars" data-id="element-423">
              <Button variant="outline" size="lg" className="font-bold" data-id="element-424">
                See More Cars <ArrowRight className="ml-2 w-5 h-5" data-id="element-425" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2: Vans */}
      <section className="py-20 bg-warm-50" data-id="element-426">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-427">
          <div className="mb-12" data-id="element-428">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-429">
              Wheelchair Accessible Vans
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-430">
              Our flagship accessible vans provide maximum interior space,
              lowered floors, and power ramps, allowing passengers to remain
              comfortably in their wheelchairs during transit.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-431">
            {vans.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-432" />)}
          </div>
          <div className="text-center" data-id="element-433">
            <Link to="/fleet/vans" data-id="element-434">
              <Button variant="outline" size="lg" className="font-bold bg-white" data-id="element-435">
                See More Vans <ArrowRight className="ml-2 w-5 h-5" data-id="element-436" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 3: Trucks */}
      <section className="py-20 bg-white" data-id="element-437">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-438">
          <div className="mb-12" data-id="element-439">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-440">
              Wheelchair Accessible Trucks
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-441">
              For those who need rugged capability without compromising on
              accessibility. Our modified trucks feature specialized lift
              systems and hand controls.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-442">
            {trucks.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-443" />)}
          </div>
          <div className="text-center" data-id="element-444">
            <Link to="/fleet/trucks" data-id="element-445">
              <Button variant="outline" size="lg" className="font-bold" data-id="element-446">
                See More Trucks <ArrowRight className="ml-2 w-5 h-5" data-id="element-447" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 4: SUVs & Crossovers */}
      <section className="py-20 bg-warm-50" data-id="element-448">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-449">
          <div className="mb-12" data-id="element-450">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-451">
              Wheelchair Accessible SUVs &amp; Crossovers
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-452">
              SUVs and crossovers offer the perfect blend of accessibility and
              style, with higher ground clearance and spacious interiors
              modified for wheelchair users.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-453">
            {suvs.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-454" />)}
          </div>
          <div className="text-center" data-id="element-455">
            <Link to="/fleet/suvs" data-id="element-456">
              <Button variant="outline" size="lg" className="font-bold bg-white" data-id="element-457">
                See More SUVs <ArrowRight className="ml-2 w-5 h-5" data-id="element-458" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}